<script setup lang="ts">
import { computed, nextTick, ref, watch, watchPostEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import AppFavicon from '../primitives/AppFavicon/AppFavicon.vue'
import { RESULT_SUMMARY_MAX_CHARS, RESULT_TITLE_MAX_CHARS, resultCut } from './article-presenter'

const props = withDefaults(
  defineProps<{
    slug: string
    titleTag: string
    metaDescription: string
    /** The brand's name, shown as the site name; falls back to the domain. */
    siteName?: string | null
    /** The brand's own domain, shown in the address line. */
    domain?: string | null
    /** Draws the listing as a static snippet with nothing editable, no hint; nothing is emitted. */
    readonly?: boolean
  }>(),
  { siteName: null, domain: null, readonly: false },
)

const emit = defineEmits<{
  updateTitle: [value: string]
  updateSummary: [value: string]
}>()

const { t } = useI18n()

const displayName = computed(() => props.siteName ?? props.domain)
const address = computed(() =>
  props.domain
    ? t('article.preview.addressWithDomain', { domain: props.domain, slug: props.slug })
    : t('article.preview.address', { slug: props.slug }),
)

/** Collapses runs of whitespace to one space and trims the ends. */
function normalise(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/** Moves the caret to the end of `element`'s text. */
function placeCaretAtEnd(element: HTMLElement): void {
  const selection = window.getSelection()
  if (!selection || document.activeElement !== element) return
  selection.selectAllChildren(element)
  selection.collapseToEnd()
}

/** The editing state of one in-place field: a draft, its focus snapshot and the commit handlers. */
function useField(
  source: () => string,
  max: number,
  placeholder: () => string,
  commit: (value: string) => void,
) {
  const element = ref<HTMLElement | null>(null)
  const draft = ref(source())
  const focused = ref(false)
  const snapshot = ref('')
  let cancelled = false

  watch(source, (value) => {
    if (!focused.value) draft.value = value
  })

  watchPostEffect(() => {
    const host = element.value
    if (!host) return
    if (focused.value) {
      if (!snapshot.value) host.replaceChildren()
      return
    }
    if (!draft.value) {
      const hint = document.createElement('span')
      hint.className = 'text-text-tertiary'
      hint.textContent = placeholder()
      host.replaceChildren(hint)
      return
    }
    const { kept, cut } = split.value
    const nodes: Node[] = [document.createTextNode(kept)]
    if (cut) {
      const tint = document.createElement('span')
      tint.className = 'bg-accent-red-wash group-focus/field:bg-transparent'
      tint.textContent = cut
      nodes.push(tint)
    }
    host.replaceChildren(...nodes)
  })

  const split = computed(() => resultCut(draft.value, max))
  const over = computed(() => split.value.cut !== '')

  function onFocus(event: Event): void {
    cancelled = false
    snapshot.value = draft.value
    focused.value = true
    const host = event.target as HTMLElement
    if (host.matches(':focus-visible')) void nextTick(() => placeCaretAtEnd(host))
  }

  function onInput(event: Event): void {
    draft.value = (event.target as HTMLElement).textContent ?? ''
  }

  function onBlur(event: Event): void {
    const next = cancelled ? source() : normalise((event.target as HTMLElement).textContent ?? '')
    cancelled = false
    focused.value = false
    draft.value = next
    if (next !== source()) commit(next)
  }

  function onEnter(event: Event): void {
    event.preventDefault()
    ;(event.target as HTMLElement).blur()
  }

  function onEscape(event: Event): void {
    cancelled = true
    ;(event.target as HTMLElement).blur()
  }

  return {
    element,
    draft,
    focused,
    snapshot,
    split,
    over,
    onFocus,
    onInput,
    onBlur,
    onEnter,
    onEscape,
  }
}

const title = useField(
  () => props.titleTag,
  RESULT_TITLE_MAX_CHARS,
  () => t('article.preview.emptyTitle'),
  (value) => emit('updateTitle', value),
)
const summary = useField(
  () => props.metaDescription,
  RESULT_SUMMARY_MAX_CHARS,
  () => t('article.preview.emptySummary'),
  (value) => emit('updateSummary', value),
)

const showCutHint = computed(() => title.over.value || summary.over.value)
const editableClass =
  'group/field break-words rounded-2 hovered:bg-bg-wash outline-none focus:outline-2 focus:outline-offset-2 focus:outline-accent-blue'
</script>

<template>
  <div class="flex min-w-0 flex-col">
    <div class="flex min-w-0 items-center gap-4">
      <span
        class="grid size-(--control-height-sm) shrink-0 place-items-center rounded-full bg-bg-tertiary"
      >
        <AppFavicon kind="web" />
      </span>
      <div class="flex min-w-0 flex-col">
        <span v-if="displayName" class="truncate text-body-sm text-text-primary">{{
          displayName
        }}</span>
        <span class="truncate text-caption text-text-secondary">{{ address }}</span>
      </div>
    </div>

    <template v-if="readonly">
      <p v-if="titleTag" class="mt-4 min-w-0 break-words text-h3-medium text-accent-blue">
        {{ title.split.value.kept
        }}<span v-if="title.split.value.cut" class="bg-accent-red-wash">{{
          title.split.value.cut
        }}</span>
      </p>
      <p v-if="metaDescription" class="mt-2 min-w-0 break-words text-body-sm text-text-secondary">
        {{ summary.split.value.kept
        }}<span v-if="summary.split.value.cut" class="bg-accent-red-wash">{{
          summary.split.value.cut
        }}</span>
      </p>
    </template>

    <template v-else>
      <div
        :ref="(el) => (title.element.value = el as HTMLElement | null)"
        contenteditable="plaintext-only"
        role="textbox"
        spellcheck="true"
        :aria-label="t('article.preview.titleLabel')"
        class="mt-4 min-h-(--text-h3-medium--line-height) min-w-0 text-h3-medium text-accent-blue"
        :class="editableClass"
        @focus="title.onFocus"
        @input="title.onInput"
        @blur="title.onBlur"
        @keydown.enter="title.onEnter"
        @keydown.esc="title.onEscape"
      ></div>

      <div
        :ref="(el) => (summary.element.value = el as HTMLElement | null)"
        contenteditable="plaintext-only"
        role="textbox"
        aria-multiline="true"
        spellcheck="true"
        :aria-label="t('article.preview.summaryLabel')"
        class="mt-2 min-h-(--text-body-sm--line-height) min-w-0 text-body-sm text-text-secondary"
        :class="editableClass"
        @focus="summary.onFocus"
        @input="summary.onInput"
        @blur="summary.onBlur"
        @keydown.enter="summary.onEnter"
        @keydown.esc="summary.onEscape"
      ></div>

      <p class="mt-4 text-caption text-text-tertiary">
        {{ t('article.preview.hint') }}
        <template v-if="showCutHint"> {{ t('article.preview.cutHint') }}</template>
      </p>
    </template>
  </div>
</template>
